import { ChangeDetectorRef, DestroyRef, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';

import { IVzSubscription, SM } from '@models/base/helper-types';
import { getRandomId } from '@models/utils/uuid';
import { finalize, Observable, Subscription } from 'rxjs';
import { HttpApiError } from '@models/api/http-api-error';
import { BaseService } from './base-service';
import { StoreService } from '@services/store.service';
import { VzRes } from '@models/utils/vz-wrappers';
import { PrefsState } from '@models/states/prefs-state';

@Injectable()
export class BaseMin {

    protected readonly __B = inject(BaseService);

    protected readonly INST         = this.__B.inst;
    protected readonly __PLATFORM   = this.__B.platform;
    protected readonly __ENV        = this.__B.env;

    protected readonly enterprise   = this.__B.isEnterprise;
    protected readonly __WEB        = this.__B.isWeb;
    protected readonly __IOS        = this.__B.isIos;
    protected readonly __ANDROID    = this.__B.isAndroid;
    protected readonly __CEF        = this.__B.isCef;
    protected readonly __WEB_REAL   = this.__B.isRealWeb;
    protected readonly __PROD       = this.__B.isProd;

    protected readonly _store?: StoreService | null       = this.__B.store;

    readonly _mobile                = this.__B.mobile;
    readonly _userId                = this.__B.userId;
    readonly _user                  = this.__B.user;
    readonly _userOpts              = this.__B.userOpts;
    readonly _orgId                 = this.__B.orgId;
    readonly _org                   = this.__B.org;
    readonly _projectId             = this.__B.projectId;
    readonly _project               = this.__B.project;
    readonly _isGuest               = this.__B.isGuest;
    readonly _granted               = this.__B.granted;
    readonly _pGranted              = this.__B.pGranted;

    protected readonly _S: SM<IVzSubscription | undefined> = {};
    protected __DESTROING = signal<boolean>(false);

    protected readonly __CID = getRandomId(8);

    protected readonly __cdr        = inject(ChangeDetectorRef, { optional: true });
    protected readonly __destroy    = inject(DestroyRef);

    readonly _error     = signal<HttpApiError | undefined>(undefined);
    readonly _loading   = signal<boolean>(true);
    readonly _working   = signal<boolean>(false);

    constructor() {
        // console.warn('[constructor]', this.__CID);
        this.__destroy.onDestroy(() => {
            // console.warn('[onDestroy]', this.__CID);
            this.__cdr?.detach();
            this.__DESTROING.set(true);
            this.__unsubscribe();
        });
    }

    protected __unsubscribe(exceptKeys?: string[]): void {
        this.__U(exceptKeys ? Object.keys(this._S).filter(id => !exceptKeys.includes(id)) : Object.keys(this._S));
    }

    protected __U(unsubscribeKeys: string[]): void {
        for (const id of unsubscribeKeys) {
            if (this._S[id]) {
                if (!this._S[id].closed) {
                    // console.log(`<${this.__CID}> unsubscribe:`, id);
                    this._S[id].unsubscribe();
                }
                // console.log(`<${this.__CID}> delete subscription:`, id);
                delete this._S[id];
            }
        }
    }

    _Loading<T>(obs: Observable<T>, opts?: { cb?: () => void }): Observable<T> {
        this._loading.set(true);
        this._Changed();
        return obs.pipe(finalize(() => {
            this._loading.set(false);
            this._Changed();
            if (opts?.cb) {
                opts?.cb();
            }
        }));
    }

    _Working<T>(obs: Observable<T>, opts?: { cb?: () => void }): Observable<T> {
        this._working.set(true);
        this._Changed();
        return obs.pipe(finalize(() => {
            this._working.set(false);
            this._Changed();
            if (opts?.cb) {
                opts?.cb();
            }
        }));
    }

    _Changed(): void {
        if (this.__cdr) {
            try {
                this.__cdr.detectChanges();
            }
            catch {
                this.__cdr.markForCheck();
            }
        }
    }

    _Error(err: HttpApiError | undefined): void {
        this._error.set(err);
        this._Changed();
    }

    _LoadRes<T, K = T>(
        obs: Observable<T>,
        res: WritableSignal<VzRes<K> | undefined>,
        opts?: { cb?: () => void, transform?: (v: T) => K }
    ): Subscription {
        res.set({ dt: Date.now(), loading: true });
        return obs.pipe(finalize(() => opts?.cb?.())).subscribe({
            next: data => res.set({ dt: Date.now(), data: opts?.transform ? opts.transform(data) : data as unknown as K }),
            error: err => res.set({ dt: Date.now(), err })
        });
    }

    _InitPref<T extends keyof PrefsState>(pk: T): WritableSignal<PrefsState[T]> {
        const pst = this.__B.store.getState('prefs');
        const sig = signal<PrefsState[typeof pk]>(pst[pk]);
        effect(() => this.__B.store.patchState('prefs', { [pk]: sig() }));
        this._S['_base_cmp_pref-' + pk] = this.__B.store.state('prefs').subscribe(st => sig.set(st[pk]));
        return sig;
    }


}
